import {
  BadgeDollarSign,
  FileSignature,
  Handshake,
  Mail,
  Rocket,
  Send,
} from "lucide-react";
import {
  CROSS_CUTTING_TAB_ICONS,
  type EntityTabItem,
} from "@/components/layout/EntityTabBar";

/**
 * The submission's tab keys and the strip that renders them.
 *
 * <p>Held in their own module rather than beside the body so the extracted
 * sections can import <c>TabKey</c> — a section that offers "go and do it on the
 * X tab" needs the key, and importing it from the body would make the body and
 * every section a cycle. <c>SubmissionDetailBody</c> re-exports all three, so
 * the drawer and the route page keep importing them from where they always
 * did.</p>
 */
export type TabKey =
  | "overview"
  | "intro-email"
  | "broker-intro"
  | "package"
  | "deal-structure"
  | "paperwork"
  | "escalation"
  | "calendar"
  | "documents"
  | "tasks"
  | "messages"
  | "communications"
  | "notes"
  | "activity";

/**
 * The tab strip, in order.
 *
 * <p><b>Overview · six entity-specific tabs · then the cross-cutting SEVEN</b> in
 * Layout Standard §3 canonical order — Documents · Tasks · Calendar · Messages ·
 * Communications · Notes · Activity.</p>
 *
 * <p>🔴 <b>Calendar HAS now moved, and this is the change §3.2 was waiting
 * for.</b> §3.2 (v1.2) recorded two surviving readings of §3 and deferred the
 * choice to a named follow-up, <c>Layout compliance — Calendar placement
 * v1.3</c>. This page previously sat on the reading that put Calendar last among
 * the entity-specific tabs, immediately before Documents; it now sits in the
 * cross-cutting block between Tasks and Messages.</p>
 *
 * <p><b>The direction was decided by arithmetic, and it is the opposite of the
 * one §3.2's own note implies.</b> 18 entity detail pages already put Calendar
 * between Tasks and Messages; two — this page and Listing — did not. Moving
 * those two closes the seam at 20/20 and costs nothing in muscle memory, because
 * 18 pages already teach the position. Moving Buyer the other way would have
 * moved 18 pages and left 17 disagreeing with the three that had changed. The
 * ruling was "make the order consistent", and only one of the two directions
 * actually delivers that.</p>
 *
 * <p>Note for anyone looking for a "v1.3": the resolution shipped as part of
 * Layout Standard v1.5. v1.3 had been reserved by name for it, but that number
 * now sorts below the already-shipped v1.4.</p>
 *
 * <p>The entity-specific tabs are the decomposition of what used to be one
 * Overview eleven sections long. The split follows the WORK rather than the
 * schema, and it runs in the order the work happens: the two intros (buyer, then
 * broker), then the package, then the deal structure Phoenix prepares, then the paperwork that has to exist
 * on file, then what was said, and finally the one act that ends the submission.
 * Overview keeps the facts a reader is looking for when they have not decided
 * which of those they are here to do.</p>
 *
 * <p>🔴 <b>The two SENDS are two tabs, not one.</b> They were one "Outreach &amp;
 * package" tab on the argument that they are one errand with two audiences. They
 * are not: the intro email goes to the BUYER to find out whether there is
 * anything worth taking to a broker, and the package goes to the LISTING BROKER
 * on the buyer's behalf. Different recipient, different purpose, and — the fact
 * that settles it — different entitlement semantics. Sending the package moves
 * <c>PackageSentAtUtc</c> and with it what the specialist has earned; sending an
 * intro email deliberately moves no count at all, which
 * <c>Sending_an_intro_email_moves_no_entitlement_count</c> pins. A tab that
 * holds both puts a control that changes an entitlement beside one that does not
 * and gives the reader nothing to tell them apart. The package-letter selector
 * travels with the package, because it is the letter the package carries.</p>
 *
 * <p>🔴 <b>Escalation is a tab, not a panel on Overview.</b> Layout Standard
 * §4.1a — an Overview may summarise, reference and link, but may not mutate or
 * send — and escalation is the most consequential mutation on the record: it
 * mints a deal. It gets the room a readiness page needs and ends in one terminal
 * confirm, and Overview links to it.</p>
 *
 * <p>Both changes are ADDITIVE under Layout Standard §4.2: entity-specific tabs
 * may be added post-v1 without amending the standard, provided they slot between
 * Overview and the cross-cutting block. Nothing already in the strip has been
 * reordered — §4.2 is explicit that reordering after release is the thing that
 * needs a migration, and this is not one.</p>
 *
 * <p>No badges anywhere: a count on a tab is a claim, and none of these fourteen
 * has a count this page can make honestly without a second read per tab.</p>
 *
 * <h3>🔴 TWO PLACES A CONVERSATION CAN LIVE — IT USED TO BE THREE</h3>
 *
 * <p>This file used to carry an argument that the entity-specific conversation
 * log could NOT be folded into the central Communications log, headed "why they
 * must stay three" and ending "do not try". PR E did exactly that, and the
 * argument is rewritten rather than deleted because the objection was RIGHT
 * about what a merge had to preserve — it was wrong only that a merge could not
 * preserve it.</p>
 *
 * <p>The objection named two structural properties the central log lacked:</p>
 *
 * <ul>
 * <li><b>Referential integrity to a submission.</b> Placements are polymorphic —
 * one <c>nvarchar(64)</c> OwnerId shared by twenty-six owner types — so no
 * foreign key could sit on it. It now has one: a typed nullable
 * <c>BuyerSubmissionId</c> beside OwnerId, carrying a real FK, with a CHECK
 * constraint holding the two in lockstep.</li>
 * <li><b>A visibility column.</b> There was none, on either table. There is now
 * <c>PackageEligible</c>, and on the PLACEMENT rather than the entry — which is
 * strictly stronger than the FK-bound store it replaces. That store made
 * cross-submission leakage impossible by giving a note nowhere else to hang; a
 * placement row does the same job while still allowing one conversation to be
 * filed against two submissions, eligible for one package and not the other.</li>
 * </ul>
 *
 * <p>So two remain, which is the app-wide norm that Buyer and Listing already
 * followed:</p>
 *
 * <ul>
 * <li><b>Messages</b> — internal staff threads. Its own audience.</li>
 * <li><b>Communications</b> — the central log, which now also carries the
 * submission's conversations, the per-placement eligibility flag, and the
 * per-entry counterparty. <c>BuyerPackageService.BuildContextAsync</c> filters
 * it to build the narrative a listing broker receives.</li>
 * </ul>
 *
 * <p><b>What was done instead, and it is presentation only.</b> The
 * entity-specific tab is now labelled <c>Submission conversation log</c> rather
 * than <c>Conversation log</c>. The word that was missing is the SCOPE: two
 * adjacent-sounding names, "Conversation log" and "Communications", gave a reader
 * nothing to choose between, and the label is where that is cheapest to fix — the
 * strip is icon-only, so the label reaches a reader as the hover/focus tooltip
 * and the <c>aria-label</c>, and length costs no pixels.</p>
 *
 * <p>🔴 <b>Communications keeps its canonical label, and that is deliberate
 * rather than an omission.</b> Twenty entity detail pages label that tab exactly
 * <c>Communications</c>; Layout Standard §4.6 prescribes it, and §3 makes the
 * cross-cutting block the muscle-memory anchor the whole standard rests on.
 * Renaming it here would leave this the ONE page in twenty that reads
 * differently — the same arithmetic that decided the Calendar move above, applied
 * the same way. The distinguishing word belongs on the tab that is ours to name.</p>
 *
 * <p><b>Nothing is reordered.</b> The three are already separated by the strip's
 * own structure: the submission-scoped log sits in the WORK block with the tabs
 * it belongs to, and the two cross-cutting ones sit five and six places to its
 * right, in the block every other record shares. §4.2 is explicit that reordering
 * after release is what needs a migration, and there is nothing here to buy by
 * doing it.</p>
 */
export const SUBMISSION_TAB_ITEMS: ReadonlyArray<EntityTabItem<TabKey>> = [
  { key: "overview", label: "Overview", icon: CROSS_CUTTING_TAB_ICONS.overview },
  // The short email to the BUYER asking whether the listing is worth pursuing.
  // An envelope rather than a paper plane: it asks a question and waits for an
  // answer, where the package below is despatched. The `intro-email` key is
  // deliberately unchanged — it is in saved links (`?tab=intro-email` opens
  // this tab) and the label is not.
  //
  // 🔴 D23 (reverses K1, by ruling): the two intros are TWO TABS, not two
  // stacked cards on one. The broker side grew a readiness strip and three
  // channels (email, text, web form) plus follow-ups — a whole workflow aimed
  // at an outside party — and a buyer card stacked above it put the wrong
  // audience one scroll away.
  { key: "intro-email", label: "Buyer intro", icon: Mail },
  // To the LISTING BROKER, before any package: the readiness strip, then the
  // email, text and web form cards and the follow-ups (D24–D33). A handshake:
  // it introduces two parties.
  { key: "broker-intro", label: "Broker intro", icon: Handshake },
  // To the LISTING BROKER, on the buyer's behalf. `Send` stays with this one
  // because it is the consequential despatch of the two — it is what moves the
  // entitlement count.
  { key: "package", label: "Submission package", icon: Send },
  // Phoenix's Potential Deal Structure (package document 3). The key it
  // replaced, `offer`, is in saved links and `?tab=` URLs, so `parseTabKey`
  // keeps it as an alias that resolves here.
  { key: "deal-structure", label: "Deal structure", icon: BadgeDollarSign },
  // What has to exist on file — the NDA, and the diligence documents the
  // listing broker sends back.
  { key: "paperwork", label: "Paperwork", icon: FileSignature },
  // Last of the entity-specific tabs because it is the last thing that happens:
  // everything to its left is the work, and this is the door out of it.
  { key: "escalation", label: "Escalate to a deal", icon: Rocket },
  { key: "documents", label: "Documents", icon: CROSS_CUTTING_TAB_ICONS.documents },
  { key: "tasks", label: "Tasks", icon: CROSS_CUTTING_TAB_ICONS.tasks },
  { key: "calendar", label: "Calendar", icon: CROSS_CUTTING_TAB_ICONS.calendar },
  { key: "messages", label: "Messages", icon: CROSS_CUTTING_TAB_ICONS.messages },
  { key: "communications", label: "Communications", icon: CROSS_CUTTING_TAB_ICONS.communications },
  { key: "notes", label: "Notes", icon: CROSS_CUTTING_TAB_ICONS.notes },
  { key: "activity", label: "Activity", icon: CROSS_CUTTING_TAB_ICONS.activity },
];

const TAB_KEYS = new Set<string>(SUBMISSION_TAB_ITEMS.map((t) => t.key));

/**
 * Which tab a raw value asks for, with an unknown one falling back to Overview.
 *
 * <p>Shared by the route wrapper (which reads `?tab=`) and by any other host of
 * this body, so the two cannot disagree about what an unrecognised tab means.
 * Callers resolve it at PARSE time — a post-mount `useEffect` that rewrites the
 * URL to a default re-keys the page's queries after first render and fetches
 * everything twice.</p>
 *
 * <p>This is also what makes the `outreach` split safe for anyone holding a
 * bookmark to the tab that no longer exists: `?tab=outreach` is unrecognised, so
 * it opens the record on Overview rather than rendering nothing.</p>
 */
export function parseTabKey(raw: string | null | undefined): TabKey {
  const alias = raw ? TAB_ALIASES.get(raw) : undefined;
  if (alias) return alias;
  return raw && TAB_KEYS.has(raw) ? (raw as TabKey) : "overview";
}

/**
 * Retired keys that saved links still carry, and the tab each now opens.
 *
 * <p>`offer` was the Indication tab's key; the tab became the Deal structure
 * tab under a new key, and a bookmark to the old one must land there rather
 * than fall back to Overview.</p>
 */
const TAB_ALIASES: ReadonlyMap<string, TabKey> = new Map<string, TabKey>([["offer", "deal-structure"]]);
